import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { io } from 'socket.io-client'
import api from '@/lib/api/client'
import CS16OverlayVariant from '@/components/overlays/CS16OverlayVariant'
import WreckfestOverlayVariant from '@/components/overlays/WreckfestOverlayVariant'
import GenericOverlayVariant from '@/components/overlays/GenericOverlayVariant'

/* Transparante OBS-overlay-page — laadt in OBS als browser-source met
   chroma-key op de body-background (of alpha-mode als OBS dat kan).

   Twee URL-vormen:
   1. /overlay/:identifier?variant=cs16       — legacy, direct op één RCON-stream
   2. /overlay/channel/:slug                  — F1-model, kanaal met live-context
                                                (welke server + welke match)

   Kanalen-modus swapt zelf de bijhorende overlay-identifier wanneer regie
   de context wisselt — OBS-URL blijft altijd hetzelfde. */

export default function OverlayPage() {
  const { identifier: paramIdent, slug } = useParams()
  const [params] = useSearchParams()
  const chroma  = params.get('chroma') || 'transparent'
  const isChannel = !!slug

  // Voor legacy-mode: alle info uit URL. Voor kanaal-mode: uit backend-context.
  const [ctx, setCtx] = useState(null)       // alleen bij kanaal-mode: {channel, server, match, team_a, team_b, tournament}
  const [identifier, setIdentifier] = useState(paramIdent || null)
  const [variant, setVariant] = useState(params.get('variant') || 'generic')
  const [byType, setByType] = useState({})
  // Kill-feed: FIFO van laatste kills, ouder dan 15s wordt uitgefaded.
  // Elk item: {id, killer, killer_team, victim, victim_team, weapon, headshot, ts}
  const [killFeed, setKillFeed] = useState([])
  const [connected, setConnected] = useState(false)

  // Chroma-key achtergrond zetten op de body.
  useEffect(() => {
    document.documentElement.style.setProperty('--overlay-bg', chroma)
    document.body.style.background = chroma
    document.body.style.margin = '0'
    return () => { document.body.style.background = '' }
  }, [chroma])

  /* ── Kanaal-context ophalen + updates ──────────────────────────── */
  useEffect(() => {
    if (!isChannel) return
    let cancel = false
    api.get(`/overlay/channels/${slug}/state`)
      .then(r => {
        if (cancel) return
        const d = r.data
        setCtx({ channel: d.channel, server: d.server, match: d.match, tournament: d.tournament, team_a: d.team_a, team_b: d.team_b })
        setIdentifier(d.identifier || null)
        setByType(d.byType || {})
        setVariant(deriveVariant(d.channel?.layout, d.server?.game_type))
      })
      .catch(() => {})
    return () => { cancel = true }
  }, [isChannel, slug])

  /* ── Socket: één connectie, roomswitch bij context-wissel ─────── */
  const socketRef = useRef(null)
  const identifierRef = useRef(identifier)

  useEffect(() => {
    if (!isChannel && !paramIdent) return
    const url = window.location.origin
    const query = isChannel ? { channel: slug } : { identifier: paramIdent }
    if (isChannel && identifier) query.identifier = identifier
    const socket = io(`${url}/overlay`, {
      query,
      transports: ['websocket', 'polling'],
      reconnection: true,
    })
    socketRef.current = socket
    identifierRef.current = identifier
    socket.on('connect', () => setConnected(true))
    socket.on('disconnect', () => setConnected(false))
    socket.on('overlay:event', (event) => {
      setByType(prev => ({ ...prev, [event.type]: event }))
      // Kills gaan naar een aparte FIFO zodat de kill-feed geschiedenis heeft.
      if (event.type === 'kill') {
        setKillFeed(prev => {
          const item = { ...event.data, ts: event.ts || Date.now(), id: Math.random() }
          // Nieuw onderaan (CS-esports conventie) — oudste bovenaan valt weg.
          return [...prev, item].slice(-8)
        })
      }
    })
    // Kanaal-context wisselt: swap overlay-identifier-room + herlaad state.
    socket.on('channel:context', (nextCtx) => {
      setCtx({
        channel: nextCtx.channel, server: nextCtx.server, match: nextCtx.match,
        tournament: nextCtx.tournament, team_a: nextCtx.team_a, team_b: nextCtx.team_b,
      })
      setVariant(deriveVariant(nextCtx.channel?.layout, nextCtx.server?.game_type))
      const newIdent = nextCtx.server?.overlay_identifier || null
      const oldIdent = identifierRef.current
      if (newIdent !== oldIdent) {
        if (oldIdent) socket.emit('overlay:leave-identifier', oldIdent)
        if (newIdent) socket.emit('overlay:join-identifier', newIdent)
        identifierRef.current = newIdent
        setIdentifier(newIdent)
        // Reset events + kill-feed — nieuwe server = nieuwe stream.
        setByType({})
        setKillFeed([])
        if (newIdent) {
          api.get(`/overlay/state/${newIdent}`)
            .then(r => setByType(r.data.byType || {}))
            .catch(() => {})
        }
      }
    })
    return () => { socket.disconnect() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChannel, slug, paramIdent])

  /* ── Room-subscribe voor identifier-events (kills, team_score, amx_status)
     Initial socket-connect gebruikt alleen `channel` in de query; de bijhorende
     identifier komt asynchroon binnen via channel-state. Deze effect emit
     join/leave bij elke wisseling. */
  useEffect(() => {
    if (!isChannel) return
    if (!identifier) return
    const s = socketRef.current
    if (!s) return
    const doJoin = () => s.emit('overlay:join-identifier', identifier)
    if (s.connected) doJoin()
    s.on('connect', doJoin)
    return () => {
      s.off('connect', doJoin)
      if (s.connected) s.emit('overlay:leave-identifier', identifier)
    }
  }, [isChannel, identifier])

  /* ── Kill-feed pruner: elke 3s droppen we kills ouder dan 15s ──── */
  useEffect(() => {
    const iv = setInterval(() => {
      const cutoff = Date.now() - 15_000
      setKillFeed(prev => prev.filter(k => (k.ts || 0) > cutoff))
    }, 3000)
    return () => clearInterval(iv)
  }, [])

  /* ── Legacy: identifier direct uit URL — één-shot state fetch ──── */
  useEffect(() => {
    if (isChannel) return
    if (!paramIdent) return
    let cancel = false
    api.get(`/overlay/state/${paramIdent}`)
      .then(r => { if (!cancel) setByType(r.data.byType || {}) })
      .catch(() => {})
    return () => { cancel = true }
  }, [isChannel, paramIdent])

  const Variant =
    variant === 'cs16'      ? CS16OverlayVariant :
    variant === 'wreckfest' ? WreckfestOverlayVariant :
    variant === 'off'       ? OffVariant :
                              GenericOverlayVariant

  // Overlay-props: layout-variants krijgen context-info (team-namen etc)
  // mee zodat "Belgian Bears 5-3 KVK" op het scherm kan ipv "team_a 5-3 team_b".
  const overlayProps = { byType, identifier, context: ctx, killFeed }

  return (
    <div style={{ minHeight: '100vh', background: chroma, color: '#fff' }}>
      <Variant {...overlayProps} />
      {params.get('debug') === '1' && (
        <div style={{
          position: 'fixed', top: 8, left: 8, fontFamily: 'monospace', fontSize: 10,
          padding: '2px 6px', background: 'rgba(0,0,0,.6)', borderRadius: 3,
          color: connected ? '#61E294' : '#FF5A5A',
        }}>
          {connected ? '● connected' : '● offline'} · {isChannel ? `ch:${slug}` : identifier}
          {ctx?.server && ` → ${ctx.server.display_name}`}
          {ctx?.match  && ` · R${ctx.match.round}·M${ctx.match.match_number}`}
        </div>
      )}
    </div>
  )
}

/* Kanaal-layout → variant-mapping. 'auto' bepaalt op basis van game_type. */
function deriveVariant(layout, gameType) {
  if (!layout || layout === 'auto') {
    if (gameType === 'cs16')       return 'cs16'
    if (gameType === 'wreckfest')  return 'wreckfest'
    return 'generic'
  }
  if (layout === 'off') return 'off'
  // 'scoreboard' / 'killfeed' / 'laptimes' / 'minimal' → mapping later uit
  // te breiden per game; voor nu vallen we terug op de game-variant.
  if (gameType === 'cs16')      return 'cs16'
  if (gameType === 'wreckfest') return 'wreckfest'
  return 'generic'
}

/* "off" — kanaal actief maar niks tonen (bv. tijdens pauze).
   OBS scene mag intussen op een andere source-composition zitten. */
function OffVariant() {
  return null
}
